import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Grid2 as Grid } from '@mui/material'
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-premium'
import { DeleteIcon, EditIcon } from '@rfh-core/icons'
import theme from '@rfh-core/theme'

import {
  ActionButton,
  ActionModal,
  ButtonsBlock,
  CustomDataGrid,
} from 'src/common/components'
import { ADD_PATH } from 'src/common/constants'
import {
  byBloemenPlantenFilter,
  byProductclusterDescriptionSort,
} from 'src/common/lib'
import { useApiClient } from 'src/common/providers/ApiClientProvider'
import { IProductclusterView } from 'src/common/services/client'
import { snackbarUtils } from 'src/common/utils'

import { CLUSTERS_CACHE_KEY, FEATURE_ROOT_PATH } from '../constants'
import { calcMarginRight, getStaticClustersColumns } from '../lib'
import { deleteClusterAsync } from '../services'
import { useProductClustersStore, useProductClusterStore } from '../stores'

type ClustersGridProps = {
  loading?: boolean
}

export const ClustersGrid: FC<ClustersGridProps> = ({
  loading = false,
  ...rest
}) => {
  const marginRight = calcMarginRight()
  const translationsKey = 'productCluster.deleteDialog'
  const { i18n, t } = useTranslation()
  const { apiClient } = useApiClient()
  const history = useHistory()
  const { bloemenPlanten, clusters, removeCluster, setMustRefetchClusters } =
    useProductClustersStore()
  const { cluster, setCluster } = useProductClusterStore()
  const [open, setOpen] = useState<boolean>(false)

  const getRowId = useCallback(
    (row: IProductclusterView) => row.productclusterID,
    []
  )

  const handleAddClick = useCallback(() => {
    history.push(`/dashboard/${FEATURE_ROOT_PATH}/${ADD_PATH}`)
  }, [history])

  const handleEditClick = useCallback(
    (row: IProductclusterView) => (): void =>
      row.productclusterID &&
      history.push(`/dashboard/${FEATURE_ROOT_PATH}/${row.productclusterID}`),
    [history]
  )

  const openDeleteDialog = useCallback(
    (row: IProductclusterView) => {
      setOpen(true)
      setCluster(row)
    },
    [setOpen, setCluster]
  )

  const closeDeleteDialog = useCallback(() => {
    setOpen(false)
    setCluster(undefined)
  }, [setCluster, setOpen])

  const handleDeleteClick = useCallback(
    (row: IProductclusterView) => (): void => {
      openDeleteDialog(row)
    },
    [openDeleteDialog]
  )

  const handleConfirmDelete = useCallback(async () => {
    if (!apiClient) {
      Promise.resolve()
    }

    try {
      closeDeleteDialog()
      removeCluster(cluster.productclusterID)
      await deleteClusterAsync(apiClient, cluster.productclusterID)
      //! the next line should be disabled till we fix ClientBase error handling
    } catch (error: any) {
      snackbarUtils.error(String(error))
    } finally {
      setMustRefetchClusters(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiClient, cluster?.productclusterID])

  const columns = useMemo((): GridColDef[] => {
    const columnsSet = getStaticClustersColumns()
    // EDIT icon
    columnsSet.push({
      field: 'edit',
      disableColumnMenu: true,
      type: 'actions',
      hideable: false,
      sortable: false,
      renderHeader: () => (
        <EditIcon
          sx={{
            color: theme.rfhColors.grey[200],
            width: '20px',
            height: '20px',
          }}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <EditIcon
          onClick={handleEditClick(params.row)}
          sx={{
            cursor: 'pointer',
            color: theme.rfhColors.grey,
            width: '20px',
            height: '20px',
          }}
        />
      ),
      flex: 1,
    })
    // DELETE icon
    columnsSet.push({
      field: 'delete',
      disableColumnMenu: true,
      type: 'actions',
      hideable: false,
      sortable: false,
      renderHeader: () => (
        <DeleteIcon
          sx={{
            color: theme.rfhColors.grey[200],
            width: '20px',
            height: '20px',
          }}
        />
      ),
      renderCell: (params: GridRenderCellParams) => (
        <DeleteIcon
          onClick={handleDeleteClick(params.row)}
          sx={{
            cursor: 'pointer',
            color: theme.rfhColors.grey,
            width: '20px',
            height: '20px',
          }}
        />
      ),
      flex: 1,
    })
    return columnsSet
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleDeleteClick, handleEditClick, i18n.language])

  return (
    <Grid container {...rest}>
      <Grid size={12} height={0} zIndex={100}>
        <ButtonsBlock marginRight={marginRight}>
          <ActionButton onClick={handleAddClick}>
            {t('common.add')}
          </ActionButton>
        </ButtonsBlock>
      </Grid>
      <Grid size={12}>
        <CustomDataGrid
          cacheKey={CLUSTERS_CACHE_KEY}
          language={i18n.language}
          getRowId={getRowId}
          columns={columns}
          rows={[
            ...clusters
              .filter(byBloemenPlantenFilter(bloemenPlanten))
              .sort(byProductclusterDescriptionSort),
          ]}
          gridProps={{
            loading,
            onRowClick: (params: GridRowParams) =>
              handleEditClick(params.row)(),
          }}
          pagination={false}
          paginationMode={'client'}
          sortingMode={'client'}
        />
      </Grid>
      <Grid size={12} height={0}>
        <ActionModal
          onCancel={closeDeleteDialog}
          onConfirm={handleConfirmDelete}
          open={open}
          translationsKey={translationsKey}
        />
      </Grid>
    </Grid>
  )
}
